import { isPlatformAndroid, isPlatformIos, isPlatformWeb } from '@24i/nxg-core-utils';
import { ASSET_TYPE, ScorerType } from '@24i/nxg-sdk-photon';
import { IPlayerEngine, ISupportedFormats } from '@24i/player-base';
import { detect } from 'detect-browser';
import { isWebView } from '@24i/nxg-sdk-higgs/src/utils';
import { mapToDrmPackageName, mapToPlayerMimetype } from './mappers';

export const scoreDrm = (
    drm: string,
    unencryptedScore: number,
    expectedDrm?: string,
    drmScore?: number
): number => {
    if (!drm || drm === 'unencrypted') return unencryptedScore;
    if (drm === expectedDrm && drmScore) return drmScore;
    return 0;
};

export const googleScorer: ScorerType = (type, drm) => {
    switch (type) {
        case ASSET_TYPE.DASH:
            return scoreDrm(drm, 7, 'widevine', 9);
        case ASSET_TYPE.HLS:
            return scoreDrm(drm, 4);
        case ASSET_TYPE.MP4:
        case ASSET_TYPE.MP3:
            return scoreDrm(drm, 1);
        default:
            return 0;
    }
};

export const appleScorer: ScorerType = (type, drm) => {
    switch (type) {
        case ASSET_TYPE.HLS:
            return scoreDrm(drm, 7, 'fairplay', 9);
        case ASSET_TYPE.MP4:
        case ASSET_TYPE.MP3:
            return scoreDrm(drm, 1);
        default:
            return 0;
    }
};

export const genericScorer: ScorerType = (type, drm) => {
    if (drm !== 'unencrypted') return 0;
    switch (type) {
        case ASSET_TYPE.DASH:
            return scoreDrm(drm, 4);
        case ASSET_TYPE.HLS:
            return scoreDrm(drm, 4);
        case ASSET_TYPE.MP4:
        case ASSET_TYPE.MP3:
            return scoreDrm(drm, 1);
        default:
            return 0;
    }
};

const selectDefaultScorer = () => {
    if (isPlatformAndroid) return googleScorer;
    if (isPlatformIos) return appleScorer;
    if (!isPlatformWeb) return genericScorer;

    const detected = detect(global.navigator?.userAgent);
    if (!detected || detected.type !== 'browser') return genericScorer;
    switch (detected.name) {
        case 'ios':
        case 'ios-webview':
        case 'safari':
            return appleScorer;
        case 'chrome':
        case 'android':
            return googleScorer;
        default:
            return genericScorer;
    }
};

export const defaultScorer = selectDefaultScorer();

export const selectSupportedScorer =
    (formats: ISupportedFormats): ScorerType =>
    (type, drm) => {
        const mimeType = mapToPlayerMimetype(type);
        const drmType = mapToDrmPackageName(drm);
        const manifest = formats.findManifest(mimeType);
        if (!manifest) return 0;
        const manifestIndex = formats.manifest.findIndex((m) => m.mimeTypes.includes(mimeType));
        if (drmType) {
            const supportedDrm = manifest.drm.find((d) => d.keySystems.includes(drmType));
            if (!supportedDrm) return 0;
        }
        /* workaround: iOS returns "manifest"s in wrong order, "native" should be last option */
        if (manifest.name === 'native') return 1;
        return Math.max(2, formats.manifest.length - manifestIndex + 1);
    };

export const pickStreamSelector = async (
    isCast: boolean,
    engine?: IPlayerEngine
): Promise<ScorerType> => {
    if (isCast) return googleScorer;
    if (isWebView() && window?.navigator?.userAgent?.includes('Android')) return googleScorer;
    if (engine) {
        const supportedFormats = await engine.getSupportedFormats();
        return selectSupportedScorer(supportedFormats);
    }
    return defaultScorer;
};
